import { Fragment, useState } from 'react'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Chip } from '@/ui/Chip/Chip'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Field, Input, Select } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Grid } from '@/ui/layout/Grid'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { StatusBadge } from '@/ui/Status/Status'
import { baseUrlPlaceholder, PROVIDER_TYPES } from '../constants/providers'
import type { SystemProviderInfo } from '../types/api'
import styles from './ProviderList.module.css'
import { RefreshFailed } from './RefreshFailed'

export interface ProviderFormBody {
  name: string
  provider_type: string
  api_key?: string
  base_url?: string
}

/**
 * Body for the update (PATCH) flow. Each field uses `string | null | undefined`
 * to mirror the server's `Option<Option<T>>`:
 *   - `undefined` → field omitted from JSON → leave column unchanged
 *   - `null`      → JSON `null`             → clear the column
 *   - `string`    → JSON `"value"`          → set the column
 * Callers should only set the fields the user actually touched.
 */
export interface ProviderUpdateBody {
  api_key?: string | null
  base_url?: string | null
}

/**
 * Tri-state edit form state for the provider edit flow.
 * Each field tracks whether the user touched it and whether they want to
 * clear the stored value.
 */
export interface EditFormState {
  api_key: string
  base_url: string
  touched_api_key: boolean
  touched_base_url: boolean
  clear_api_key: boolean
  clear_base_url: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- shared tri-state default consumed by ProviderList and its test
export const EMPTY_EDIT_FORM: EditFormState = {
  api_key: '',
  base_url: '',
  touched_api_key: false,
  touched_base_url: false,
  clear_api_key: false,
  clear_base_url: false,
}

/**
 * Translate the tri-state edit form into a PATCH body.
 *
 * Per field: `clear` → null (clear column), `touched` → string (set column),
 * untouched → omitted (undefined → leave column unchanged).
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure serializer consumed by ProviderList and its test
export function buildProviderUpdateBody(
  form: EditFormState,
): ProviderUpdateBody {
  const body: ProviderUpdateBody = {}
  if (form.clear_api_key) body.api_key = null
  else if (form.touched_api_key && form.api_key) body.api_key = form.api_key
  if (form.clear_base_url) body.base_url = null
  else if (form.touched_base_url) body.base_url = form.base_url
  return body
}

interface Props {
  providers: SystemProviderInfo[]
  isLoading: boolean
  isError?: boolean
  onRetry?: () => void
  canManage: boolean
  /** Shows the add row (or the empty state's create), which opens the form. */
  canCreate?: boolean
  /** The block empty state: the list is the whole tab (README, A list creates from its end). */
  emptyText: string
  autoFocusName?: boolean
  creating: boolean
  onCreatingChange: (open: boolean) => void
  onCreate: (body: ProviderFormBody) => void
  isCreating: boolean
  createError?: string
  onUpdate: (providerId: string, body: ProviderUpdateBody) => void
  isUpdating: boolean
  updateError?: string
  onDelete: (providerId: string) => void
  isDeleting: boolean
}

/**
 * Presentational provider list + create form, shared by the admin
 * (system-wide `providersApi`) and workspace-settings (`workspaceProvidersApi`)
 * call sites. Owns no data fetching/mutations — each call site keeps its own,
 * since the underlying resources are genuinely different (system vs.
 * per-workspace credentials). The caller owns `creating`; the create action
 * is the list's last row, or the empty state's button (SectionHeader · Rules).
 */
export function ProviderList({
  providers,
  isLoading,
  isError = false,
  onRetry,
  canManage,
  emptyText,
  autoFocusName = false,
  creating,
  onCreatingChange,
  canCreate = false,
  onCreate,
  isCreating,
  createError,
  onUpdate,
  isUpdating,
  updateError,
  onDelete,
  isDeleting,
}: Props) {
  const showSkeleton = useSkeletonDelay(isLoading)
  const [form, setForm] = useState<ProviderFormBody>({
    name: '',
    provider_type: 'anthropic',
    api_key: '',
    base_url: '',
  })
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const providerToDelete = providers.find(
    (p) => p.provider_id === confirmDelete,
  )
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM)

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) return
    onCreate({
      name: form.name.trim(),
      provider_type: form.provider_type,
      api_key: form.api_key || undefined,
      base_url: form.base_url || undefined,
    })
    setForm({ name: '', provider_type: 'anthropic', api_key: '', base_url: '' })
    onCreatingChange(false)
  }

  function startEdit(p: SystemProviderInfo) {
    setEditingId(p.provider_id)
    setConfirmDelete(null)
    setEditForm({
      ...EMPTY_EDIT_FORM,
      base_url: p.base_url ?? '',
    })
  }

  function submitEdit(providerId: string) {
    onUpdate(providerId, buildProviderUpdateBody(editForm))
    setEditingId(null)
  }

  const showForm = creating && canManage
  const createForm = (
    <form onSubmit={handleCreate}>
      <Stack gap="space-16">
        <Grid columns={1} mdColumns={2} gap="space-16">
          <Field label={settingsCopy.providers.name}>
            <Input
              autoFocus={autoFocusName}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          <Field
            label={settingsCopy.providers.type}
            help={settingsCopy.providers.typeHelp[form.provider_type]}
          >
            <Select
              value={form.provider_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, provider_type: e.target.value }))
              }
            >
              {PROVIDER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Field>
        </Grid>
        <Field label={settingsCopy.providers.apiKey}>
          <Input
            type="password"
            value={form.api_key}
            onChange={(e) =>
              setForm((f) => ({ ...f, api_key: e.target.value }))
            }
          />
        </Field>
        <Field
          label={settingsCopy.providers.baseUrl}
          hint={
            form.provider_type === 'openai'
              ? settingsCopy.providers.required
              : undefined
          }
        >
          <Input
            value={form.base_url}
            onChange={(e) =>
              setForm((f) => ({ ...f, base_url: e.target.value }))
            }
            placeholder={baseUrlPlaceholder(form.provider_type)}
          />
        </Field>
        <Inline gap="space-8">
          <Button
            type="submit"
            variant="primary"
            disabled={!form.name.trim()}
            loading={isCreating}
          >
            {settingsCopy.providers.create}
          </Button>
          <Button variant="outline" onClick={() => onCreatingChange(false)}>
            {settingsCopy.providers.cancel}
          </Button>
        </Inline>
        {createError && <Alert tone="danger">{createError}</Alert>}
      </Stack>
    </form>
  )
  const addButton = (
    <Button variant="ghost" onClick={() => onCreatingChange(true)}>
      <Icon name="plus" size="icon-embedded" />
      {settingsCopy.providers.add}
    </Button>
  )

  return (
    <Stack as="section" gap="space-12" aria-busy={isLoading}>
      <SectionHeader rule>{settingsCopy.providers.heading}</SectionHeader>

      {isLoading ? (
        showSkeleton && <Skeleton lines={3} />
      ) : isError && providers.length === 0 ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{settingsCopy.loadFailed.providers}</Text>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {settingsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : (
        <>
          {isError && onRetry && (
            <RefreshFailed
              message={settingsCopy.refreshFailed.providers}
              onRetry={onRetry}
            />
          )}
          {providers.length === 0 && !showForm && (
            <EmptyState variant="inline" description={emptyText} />
          )}
          <RowList aria-label={settingsCopy.providers.listLabel}>
            {providers.map((p) => (
              <Fragment key={p.provider_id}>
                <RowListItem>
                  <Text tone="text-ghost">
                    <Icon name="lightning-bolt" size="icon-embedded" />
                  </Text>
                  <Inline gap="space-8" className={styles.main}>
                    <Text>{p.name}</Text>
                    <Chip variant="kind" label={p.provider_type} />
                    {p.base_url && (
                      <Text variant="code" tone="text-mute">
                        {p.base_url}
                      </Text>
                    )}
                  </Inline>
                  {p.has_key ? (
                    <StatusBadge tone="success">
                      {settingsCopy.providers.keySet}
                    </StatusBadge>
                  ) : (
                    <StatusBadge tone="warning">
                      {settingsCopy.providers.noKey}
                    </StatusBadge>
                  )}
                  {canManage && (
                    <Inline gap="space-8">
                      <Button
                        variant="ghost"
                        disabled={isUpdating || isDeleting}
                        onClick={() => startEdit(p)}
                      >
                        {settingsCopy.providers.edit}
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={isUpdating || isDeleting}
                        onClick={() => setConfirmDelete(p.provider_id)}
                      >
                        {settingsCopy.providers.delete}
                      </Button>
                    </Inline>
                  )}
                </RowListItem>
                {editingId === p.provider_id && (
                  <RowListItem className={styles.edit}>
                    <Stack gap="space-16">
                      <Field label={settingsCopy.providers.apiKey}>
                        <Input
                          type="password"
                          value={editForm.api_key}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              api_key: e.target.value,
                              touched_api_key: true,
                              clear_api_key: false,
                            }))
                          }
                          placeholder={
                            p.has_key
                              ? settingsCopy.providers.keepKeyPlaceholder
                              : undefined
                          }
                        />
                      </Field>
                      {p.has_key && !editForm.clear_api_key && (
                        <Inline gap="space-8">
                          <Button
                            variant="quiet"
                            onClick={() =>
                              setEditForm((f) => ({
                                ...f,
                                clear_api_key: true,
                                touched_api_key: true,
                                api_key: '',
                              }))
                            }
                          >
                            {settingsCopy.providers.clearKey}
                          </Button>
                        </Inline>
                      )}
                      {editForm.clear_api_key && (
                        <Alert tone="warning">
                          <Inline gap="space-8" align="baseline" wrap>
                            <Text>{settingsCopy.providers.keyCleared}</Text>
                            <Button
                              variant="ghost"
                              onClick={() =>
                                setEditForm((f) => ({
                                  ...f,
                                  clear_api_key: false,
                                  touched_api_key: false,
                                }))
                              }
                            >
                              {settingsCopy.providers.keepKey}
                            </Button>
                          </Inline>
                        </Alert>
                      )}
                      <Field
                        label={settingsCopy.providers.baseUrl}
                        hint={
                          p.provider_type === 'openai'
                            ? settingsCopy.providers.required
                            : undefined
                        }
                      >
                        <Input
                          value={editForm.base_url}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              base_url: e.target.value,
                              touched_base_url: true,
                              clear_base_url: false,
                            }))
                          }
                          placeholder={baseUrlPlaceholder(p.provider_type)}
                        />
                      </Field>
                      {p.base_url && !editForm.clear_base_url && (
                        <Inline gap="space-8">
                          <Button
                            variant="quiet"
                            onClick={() =>
                              setEditForm((f) => ({
                                ...f,
                                clear_base_url: true,
                                touched_base_url: true,
                                base_url: '',
                              }))
                            }
                          >
                            {settingsCopy.providers.clearUrl}
                          </Button>
                        </Inline>
                      )}
                      {editForm.clear_base_url && (
                        <Inline gap="space-8" align="baseline">
                          <Text variant="caption" tone="text-mute">
                            {settingsCopy.providers.urlCleared}
                          </Text>
                          <Button
                            variant="quiet"
                            onClick={() =>
                              setEditForm((f) => ({
                                ...f,
                                clear_base_url: false,
                                touched_base_url: false,
                                base_url: p.base_url ?? '',
                              }))
                            }
                          >
                            {settingsCopy.providers.keepUrl}
                          </Button>
                        </Inline>
                      )}
                      <Inline gap="space-8">
                        <Button
                          variant="primary"
                          loading={isUpdating}
                          onClick={() => submitEdit(p.provider_id)}
                        >
                          {settingsCopy.providers.save}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setEditingId(null)}
                        >
                          {settingsCopy.providers.cancel}
                        </Button>
                      </Inline>
                      {updateError && (
                        <Alert tone="danger">{updateError}</Alert>
                      )}
                    </Stack>
                  </RowListItem>
                )}
              </Fragment>
            ))}
            {showForm ? (
              <RowListItem className={styles.edit}>{createForm}</RowListItem>
            ) : (
              canCreate && <RowListItem>{addButton}</RowListItem>
            )}
          </RowList>
        </>
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title={settingsCopy.providers.deleteDialog.title}
        tone="danger"
        confirmLabel={settingsCopy.providers.deleteDialog.confirm}
        description={
          providerToDelete
            ? settingsCopy.providers.deleteDialog.description(
                providerToDelete.name,
              )
            : undefined
        }
        loading={isDeleting}
        onConfirm={() => {
          if (confirmDelete) onDelete(confirmDelete)
          setConfirmDelete(null)
        }}
        onClose={() => setConfirmDelete(null)}
      />
    </Stack>
  )
}
